import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Eye } from 'lucide-react';
import { getBestImageUrl } from '@/lib/utils';

interface TransferPageHeroProps {
  businessName?: string;
  businessDescription?: string;
  businessAddress?: string;
  businessHandle?: string;
  businessImageId?: string | null;
  businessMedia?: { media_id: string; rank?: number }[] | null;
  role: 'SENDER' | 'RECEIVER' | null;
  onBack?: () => void;
}

export const TransferPageHero: React.FC<TransferPageHeroProps> = ({
  businessName,
  businessDescription,
  businessAddress,
  businessHandle,
  businessImageId,
  businessMedia,
  role,
  onBack,
}) => {
  const navigate = useNavigate();

  const backgroundImage = getBestImageUrl(businessImageId || null, businessMedia || null, '/placeholder-club.jpg');
  const heading = role === 'SENDER' ? 'Manage Transfer' : 'Accept Transfer';

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="relative min-h-[20rem] overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      {/* Fallback gradient background */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900"
        style={{ zIndex: -1 }}
      />

      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col pt-8 pb-10">
        {/* Back button */}
        {role === 'SENDER' && (
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-300 hover:text-white mb-6 w-fit transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
        )}

        <div className="mt-auto space-y-4">
          {/* Main Heading */}
          <h1 className="text-4xl lg:text-6xl font-serif font-bold text-white tracking-tight">
            {heading}
          </h1>

          {/* Business Name */}
          {businessName && (
            <p className="text-xl lg:text-2xl text-gray-200 font-medium">
              {businessName}
            </p>
          )}

          {/* Location */}
          {businessAddress && (
            <div className="flex items-center space-x-2 text-blue-400 font-medium">
              <MapPin className="w-4 h-4" />
              <span>{businessAddress}</span>
            </div>
          )}

          {/* Description */}
          {businessDescription && (
            <p className="text-base text-gray-300 max-w-3xl line-clamp-2">
              {businessDescription}
            </p>
          )}

          {/* Link to Venue Page */}
          {businessHandle && (
            <button
              onClick={() => navigate(`/venues/${businessHandle}`)}
              className="inline-flex items-center px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg border border-white/30 transition-colors font-medium mt-2"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Venue Page
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferPageHero;
